import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Volume2 } from 'lucide-react';
import { UnifiedCampaign } from '@/types/unifiedCampaign';
import { useOptimizedSupabaseScripts } from '@/hooks/useOptimizedSupabaseScripts';
import { useSupabaseProspectLists } from '@/hooks/useSupabaseProspectLists';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { LiveCampaignSettings } from '@/components/LiveCampaignSettings';
import {
  fetchElevenLabsVoices,
  previewElevenLabsVoice,
  readCachedVoices,
  writeCachedVoices,
  EMPTY_VOICES_MESSAGE,
} from '@/lib/elevenLabsVoices';

interface CampaignEditDialogProps {
  campaign: UnifiedCampaign | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (id: string, updates: Partial<UnifiedCampaign>) => Promise<void>;
  /** Persists a partial update immediately without closing or toasting.
      Falls back to onSave if not provided. */
  onPersist?: (id: string, updates: Partial<UnifiedCampaign>) => Promise<void>;
}


export function CampaignEditDialog({
  campaign,
  open,
  onOpenChange,
  onSave,
  onPersist,
}: CampaignEditDialogProps) {
  const { scripts } = useOptimizedSupabaseScripts();
  const { prospectLists } = useSupabaseProspectLists();
  const { apiKeys, user } = useAuth();
  const { toast } = useToast();

  const [name, setName] = useState('');
  const [scriptId, setScriptId] = useState('');
  const [outboundScriptId, setOutboundScriptId] = useState('');
  const [inboundScriptId, setInboundScriptId] = useState('');
  const [prospectListId, setProspectListId] = useState('');
  const [voiceId, setVoiceId] = useState('');
  const [availableVoices, setAvailableVoices] = useState<any[]>([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [previewingVoice, setPreviewingVoice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const hasElevenLabsKeys = !!apiKeys?.elevenlabs;

  // Load voices from cache or, failing that, the edge function.
  useEffect(() => {
    if (!open || !hasElevenLabsKeys) return;

    const cached = readCachedVoices();
    if (cached) {
      setAvailableVoices(cached);
      return;
    }

    const loadVoices = async () => {
      setLoadingVoices(true);
      try {
        const voices = await fetchElevenLabsVoices();
        setAvailableVoices(voices);
        writeCachedVoices(voices);

        if (voices.length === 0) {
          toast({
            title: 'No voices found',
            description: EMPTY_VOICES_MESSAGE,
            variant: 'destructive',
          });
        }
      } catch (error) {
        toast({
          title: 'Could not load voices',
          description:
            error instanceof Error ? error.message : 'Failed to load voices.',
          variant: 'destructive',
        });
      } finally {
        setLoadingVoices(false);
      }
    };

    loadVoices();
  }, [open, hasElevenLabsKeys]);

  useEffect(() => {
    if (!campaign || !open) return;
    setName(campaign.name || '');
    setVoiceId(campaign.voice_id || '');
    setProspectListId(campaign.prospect_list_id || '');
    if (campaign.campaign_type === 'outbound_inbound') {
      setOutboundScriptId(campaign.outbound_script_id || '');
      setInboundScriptId(campaign.inbound_script_id || '');
    } else {
      setScriptId(campaign.script_id || '');
    }
  }, [campaign, open]);

  const previewVoice = async () => {
    if (!voiceId) return;
    setPreviewingVoice(voiceId);
    try {
      const audioUrl = await previewElevenLabsVoice(voiceId);
      await new Audio(audioUrl).play();
      toast({ title: 'Voice Preview', description: 'Playing voice sample...' });
    } catch (error) {
      toast({
        title: 'Preview Error',
        description:
          error instanceof Error ? error.message : 'Failed to preview voice',
        variant: 'destructive',
      });
    } finally {
      setPreviewingVoice(null);
    }
  };

  if (!campaign) return null;

  const isActive = campaign.status === 'active';
  const isInbound = campaign.campaign_type === 'inbound';
  const isOutboundInbound = campaign.campaign_type === 'outbound_inbound';

  const outboundScripts = scripts.filter((s) => s.mode === 'outbound');
  const inboundScripts = scripts.filter((s) => s.mode === 'inbound');

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates: Partial<UnifiedCampaign> = { name, voice_id: voiceId };

      if (isOutboundInbound) {
        updates.outbound_script_id = outboundScriptId;
        updates.inbound_script_id = inboundScriptId;
        updates.script_id = outboundScriptId;
      } else {
        updates.script_id = scriptId;
      }

      if (!isInbound && !isActive) {
        updates.prospect_list_id = prospectListId || undefined;
      }

      await onSave(campaign.id, updates);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const selectedVoiceName = availableVoices.find((v) => v.voice_id === voiceId)?.name;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[520px] max-h-[90vh] flex flex-col overflow-hidden'>
        <DialogHeader>
          <DialogTitle>Edit Campaign</DialogTitle>
        </DialogHeader>

        {/* Scroll the body only, so the dialog's rounded corners stay intact.
            -mr-2/pr-2 nudges the scrollbar off the edge for a clean look. */}
        <div className='flex-1 space-y-4 overflow-y-auto overflow-x-hidden py-2 -mr-2 pr-2'>
          {/* Campaign name */}
          <div className='space-y-1.5'>
            <Label>Campaign Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          {/* Voice selector */}
          <div className='space-y-1.5'>
            <Label>Voice (AI Agent)</Label>
            <Select value={voiceId} onValueChange={setVoiceId}>
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    !hasElevenLabsKeys
                      ? 'No ElevenLabs key configured'
                      : loadingVoices
                      ? 'Loading voices...'
                      : 'Select a voice'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {!hasElevenLabsKeys ? (
                  <SelectItem value='__no_key' disabled>
                    Configure ElevenLabs API key in API Settings
                  </SelectItem>
                ) : loadingVoices ? (
                  <SelectItem value='__loading' disabled>
                    Loading voices...
                  </SelectItem>
                ) : availableVoices.length === 0 ? (
                  <SelectItem value='__none' disabled>
                    No voices available
                  </SelectItem>
                ) : (
                  availableVoices.map((v) => (
                    <SelectItem key={v.voice_id} value={v.voice_id}>
                      {v.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {voiceId && selectedVoiceName && (
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={previewVoice}
                disabled={previewingVoice === voiceId}
              >
                <Volume2 className='h-4 w-4 mr-2' />
                {previewingVoice === voiceId ? 'Playing...' : '🔊 Preview Voice'}
              </Button>
            )}
          </div>

          {/* Script selector(s) */}
          {!isOutboundInbound && (
            <div className='space-y-1.5'>
              <Label>Script</Label>
              <Select value={scriptId} onValueChange={setScriptId}>
                <SelectTrigger>
                  <SelectValue placeholder='Select a script' />
                </SelectTrigger>
                <SelectContent>
                  {scripts.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {isOutboundInbound && (
            <>
              <div className='space-y-1.5'>
                <Label>Outbound Script</Label>
                <Select value={outboundScriptId} onValueChange={setOutboundScriptId}>
                  <SelectTrigger>
                    <SelectValue placeholder='Select outbound script' />
                  </SelectTrigger>
                  <SelectContent>
                    {outboundScripts.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className='space-y-1.5'>
                <Label>Inbound Script</Label>
                <Select value={inboundScriptId} onValueChange={setInboundScriptId}>
                  <SelectTrigger>
                    <SelectValue placeholder='Select inbound script' />
                  </SelectTrigger>
                  <SelectContent>
                    {inboundScripts.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {/* Prospect list — outbound only, locked when active */}
          {!isInbound && (
            <div className='space-y-1.5'>
              <Label>
                Prospect List
                {isActive && (
                  <span className='ml-2 text-xs text-muted-foreground'>
                    (locked while active)
                  </span>
                )}
              </Label>
              <Select
                value={prospectListId}
                onValueChange={setProspectListId}
                disabled={isActive}
              >
                <SelectTrigger>
                  <SelectValue placeholder='Select a list' />
                </SelectTrigger>
                <SelectContent>
                  {prospectLists.map((pl) => (
                    <SelectItem key={pl.id} value={pl.id}>
                      {pl.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Live lead-intake settings — outbound campaigns only.
              Persists immediately (does not close the dialog). */}
          {!isInbound && (
            <LiveCampaignSettings
              campaign={campaign}
              currentUserId={user?.id}
              onPersist={(updates) => (onPersist ?? onSave)(campaign.id, updates)}
            />
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !name.trim()}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
